import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import html2canvas from 'html2canvas';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const PDFExtractTest = () => {
  const [extractedText, setExtractedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const pdfViewerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const uploadPdfToStorage = async (file: File) => {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;
    
    console.log('Uploading PDF to storage...');
    const { error } = await supabase.storage
      .from('uploaded-documents')
      .upload(fileName, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from('uploaded-documents')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast({
        title: "Invalid file type",
        description: "Please select a PDF file",
        variant: "destructive"
      });
      return;
    }

    // Check authentication first
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to upload PDFs",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    setExtractedText('');
    setUploadedPdfUrl(null);
    setScreenshotUrl(null);
    setPdfPages([]);

    try {
      console.log('=== Starting PDF processing ===');
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      // Step 1: Upload PDF to storage
      console.log('Step 1: Uploading PDF to storage...');
      const pdfUrl = await uploadPdfToStorage(file);
      setUploadedPdfUrl(pdfUrl);
      console.log('PDF uploaded successfully to:', pdfUrl);

      // Step 2: Render PDF using PDF.js
      console.log('Step 2: Rendering PDF with PDF.js...');
      await renderPdfPages(pdfUrl);
      console.log('PDF rendering completed');

      // Log success
      console.log('=== PDF processing completed successfully ===');

    } catch (error) {
      console.error('=== PDF processing failed ===');
      console.error('Error details:', error);
      toast({
        title: "Processing failed", 
        description: error instanceof Error ? error.message : "Failed to process PDF",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      console.log('=== PDF processing finished (finally block) ===');
    }
  };

  const renderPdfPages = async (pdfUrl: string) => {
    try {
      console.log('Starting PDF.js rendering...');
      console.log('PDF URL:', pdfUrl);
      console.log('PDF.js version:', pdfjsLib.version);
      console.log('Worker source:', pdfjsLib.GlobalWorkerOptions.workerSrc);
      
      console.log('Loading PDF document...');
      const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
      console.log('PDF loaded successfully:', {
        numPages: pdf.numPages
      });
      
      const pageImages: string[] = [];

      // Render first 3 pages (or all if less than 3)
      const numPages = Math.min(pdf.numPages, 3);
      console.log(`Will render ${numPages} pages`);
      
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        console.log(`Rendering page ${pageNum}...`);
        
        const page = await pdf.getPage(pageNum);
        console.log(`Page ${pageNum} loaded, dimensions:`, {
          width: page.view[2],
          height: page.view[3]
        });
        
        const viewport = page.getViewport({ scale: 1.5 });
        console.log(`Viewport created for page ${pageNum}:`, {
          width: viewport.width,
          height: viewport.height
        });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        if (context) {
          console.log(`Starting render for page ${pageNum}...`);
          await page.render({
            canvasContext: context,
            viewport: viewport,
            canvas: canvas
          }).promise;
          
          const imageData = canvas.toDataURL();
          pageImages.push(imageData);
          console.log(`Page ${pageNum} rendered successfully, data URL length:`, imageData.length);
        } else {
          throw new Error(`Could not get canvas context for page ${pageNum}`);
        }
      }

      setPdfPages(pageImages);
      console.log(`Successfully rendered ${pageImages.length} PDF pages`);
      
      toast({
        title: "PDF Loaded!",
        description: `Rendered ${pageImages.length} pages`,
        variant: "default"
      });
    } catch (error) {
      console.error('=== PDF rendering failed ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Full error object:', error);
      
      // Try to provide more specific error information
      let errorMessage = "Could not render PDF pages";
      if (error.message.includes('Loading')) {
        errorMessage = "Failed to load PDF - file may be corrupted or password protected";
      } else if (error.message.includes('CORS')) {
        errorMessage = "PDF access blocked - CORS issue";
      } else if (error.message.includes('worker')) {
        errorMessage = "PDF.js worker failed to load";
      }
      
      toast({
        title: "PDF rendering failed",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const takeScreenshot = async () => {
    const pdfViewer = pdfViewerRef.current;
    if (!pdfViewer || pdfPages.length === 0) {
      toast({
        title: "Error",
        description: "No PDF pages to screenshot",
        variant: "destructive"
      });
      return;
    }

    try {
      console.log('Taking screenshot of rendered PDF...');
      const canvas = await html2canvas(pdfViewer, {
        useCORS: true,
        scale: 2,
        logging: false
      });
      
      const screenshotDataUrl = canvas.toDataURL('image/png');
      setScreenshotUrl(screenshotDataUrl);
      
      toast({
        title: "Screenshot captured!",
        description: "PDF screenshot ready for processing",
        variant: "default"
      });
    } catch (error) {
      console.error('Screenshot error:', error);
      toast({
        title: "Screenshot failed",
        description: "Could not capture PDF screenshot",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>PDF Text Scraping Test</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <p className="text-yellow-800 text-sm">
                <strong>Note:</strong> You must be signed in to upload PDFs. This test uploads the PDF to Supabase storage, renders it with PDF.js, and allows you to take screenshots.
              </p>
            </div>
            <Input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              disabled={isProcessing}
            />
            {isProcessing && (
              <div className="text-primary">Processing PDF...</div>
            )}
          </div>
        </CardContent>
      </Card>

      {pdfPages.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>PDF Pages (Rendered with PDF.js)</CardTitle>
          </CardHeader>
          <CardContent>
            <div ref={pdfViewerRef} className="space-y-4">
              {pdfPages.map((pageImage, index) => (
                <div key={index} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-3 py-2 text-sm font-medium">
                    Page {index + 1}
                  </div>
                  <img 
                    src={pageImage} 
                    alt={`PDF Page ${index + 1}`}
                    className="w-full"
                  />
                </div>
              ))}
            </div>
            <Button 
              onClick={takeScreenshot}
              className="mt-4"
              variant="outline"
            >
              Take Screenshot of All Pages
            </Button>
          </CardContent>
        </Card>
      )}

      {screenshotUrl && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-blue-600">Screenshot Captured</CardTitle>
          </CardHeader>
          <CardContent>
            <img 
              src={screenshotUrl} 
              alt="PDF Screenshot" 
              className="w-full border rounded-lg"
            />
          </CardContent>
        </Card>
      )}

      {extractedText && (
        <Card>
          <CardHeader>
            <CardTitle className="text-green-600">
              Scraped Text ({extractedText.length} characters)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <pre className="whitespace-pre-wrap text-sm font-mono text-green-800 max-h-96 overflow-y-auto">
                {extractedText}
              </pre>
            </div>
            <Button 
              onClick={() => navigator.clipboard.writeText(extractedText)}
              className="mt-4"
              variant="outline"
            >
              Copy to Clipboard
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PDFExtractTest;